// /mis-reservas — listado de reservas del usuario logado.
//
// Acceso protegido: si !isAuthenticated → redirige a /horarios (donde puede
// reservar como guest) y abrir el AuthModal de login no es responsabilidad
// de esta página. La rehidratación es asíncrona, así que mientras `loading`
// es true mostramos skeleton; luego decidimos.

import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { useAuth, readToken } from '../context/AuthContext';
import { getMisReservas } from '../lib/api';
import { parseFecha, formateaCodigoReserva } from '../lib/fechas';

function localePara(lang) {
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return map[lang] || 'es-ES';
}

function recortaHora(h) {
  if (!h) return '';
  return h.slice(0, 5);
}

function ItemReserva({ reserva, lang }) {
  const { t } = useTranslation();
  const fecha = parseFecha(reserva.fecha_clase);
  const dia = new Intl.DateTimeFormat(localePara(lang), { day: 'numeric', timeZone: 'UTC' }).format(fecha);
  const mes = new Intl.DateTimeFormat(localePara(lang), { month: 'short', timeZone: 'UTC' }).format(fecha);
  const claseTipo = reserva.horario?.clase_tipo;
  const claseNombre = claseTipo?.[`nombre_${lang}`] || claseTipo?.nombre_es || '—';
  const profesor = reserva.horario?.profesor?.nombre_publico;
  const horaIni = recortaHora(reserva.horario?.hora_inicio);
  const codigo = formateaCodigoReserva(reserva.codigo_reserva);
  const estado = reserva.estado;

  return (
    <Link to={`/reserva/${reserva.codigo_reserva}`} className="mr-item">
      <div className="mr-fecha">
        <span className="mr-fecha-dia">{dia}</span>
        <span className="mr-fecha-mes">{mes}</span>
      </div>
      <div className="mr-info">
        <span className="mr-info-titulo">{claseNombre}</span>
        <span className="mr-info-sub">
          {horaIni}
          {profesor && <> · {t('reservas.con_profesor', { nombre: profesor })}</>}
        </span>
      </div>
      <div className="mr-meta">
        <span className={'mr-estado mr-estado--' + estado}>
          {t('reserva_detail.estado.' + estado, { defaultValue: estado })}
        </span>
        <span className="mr-codigo">{codigo}</span>
      </div>
    </Link>
  );
}

export default function MisReservas() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [datos, setDatos] = useState({ data: null, error: null, loading: true });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setDatos({ data: null, error: null, loading: false });
      return;
    }
    let cancelled = false;
    setDatos({ data: null, error: null, loading: true });
    const token = readToken();
    getMisReservas(token)
      .then((json) => {
        if (cancelled) return;
        setDatos({ data: json, error: null, loading: false });
      })
      .catch((err) => {
        if (cancelled) return;
        setDatos({ data: null, error: err.message || 'error', loading: false });
      });
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated]);

  if (authLoading) {
    return (
      <main className="mis-reservas-page">
        <div className="mr-vacio"><Loader2 size={20} className="spin" strokeWidth={1.5} /></div>
      </main>
    );
  }

  if (!isAuthenticated) {
    // No mandar al user a la home; mejor a /horarios donde puede reservar.
    return <Navigate to="/horarios" replace />;
  }

  if (datos.loading) {
    return (
      <main className="mis-reservas-page">
        <header className="mr-head">
          <span className="section-eyebrow">{t('auth.mis_reservas.eyebrow')}</span>
          <h1 className="mr-titulo">{t('auth.mis_reservas.titulo')}</h1>
        </header>
        <div className="mr-vacio"><Loader2 size={20} className="spin" strokeWidth={1.5} /></div>
      </main>
    );
  }

  if (datos.error) {
    return (
      <main className="mis-reservas-page">
        <header className="mr-head">
          <span className="section-eyebrow">{t('auth.mis_reservas.eyebrow')}</span>
          <h1 className="mr-titulo">{t('auth.mis_reservas.titulo')}</h1>
        </header>
        <div className="mr-vacio">{t('auth.mis_reservas.error')}</div>
      </main>
    );
  }

  const proximas = datos.data?.proximas || [];
  const pasadas = datos.data?.pasadas || [];
  const total = (datos.data?.total) || 0;

  return (
    <main className="mis-reservas-page">
      <header className="mr-head">
        <span className="section-eyebrow">{t('auth.mis_reservas.eyebrow')}</span>
        <h1 className="mr-titulo">{t('auth.mis_reservas.titulo')}</h1>
        <p className="mr-sub">
          {t('auth.mis_reservas.sub', { nombre: user?.nombre || '' })}
        </p>
      </header>

      {total === 0 && (
        <div className="mr-vacio">
          {t('auth.mis_reservas.vacio_total')}{' '}
          <Link to="/horarios">{t('auth.mis_reservas.vacio_cta')}</Link>
        </div>
      )}

      {proximas.length > 0 && (
        <section className="mr-grupo">
          <h2 className="mr-grupo-titulo">{t('auth.mis_reservas.proximas')}</h2>
          <div className="mr-lista">
            {proximas.map((r) => <ItemReserva key={r.id} reserva={r} lang={lang} />)}
          </div>
        </section>
      )}

      {pasadas.length > 0 && (
        <section className="mr-grupo">
          <h2 className="mr-grupo-titulo">{t('auth.mis_reservas.pasadas')}</h2>
          <div className="mr-lista">
            {pasadas.map((r) => <ItemReserva key={r.id} reserva={r} lang={lang} />)}
          </div>
        </section>
      )}
    </main>
  );
}
